import AppLayout from "./actprep-ui/components/AppLayout";
import AuthenticatingPage from "./actprep-ui/pages/AuthenticatingPage";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { urls } from "./actprep-ui/utils/urls";
import Redirect from "./actprep-ui/components/Redirect/";
import ErrorPage from "./actprep-ui/pages/ErrorPage";
import useAccess from "./actprep-ui/hooks/auth/useAccess";
import { Home, School, Security } from "@mui/icons-material";
import { lazy, Suspense } from "react";
import { useProfile } from "./actprep-ui/context/ProfileContext";
import { groups } from "./actprep-ui/constants/auth";
import SkeletonLayout from "./actprep-ui/components/SkeletonLayout/SkeletonLayout";
import {
  useImpersonation,
  ImpersonationProvider,
} from "./actprep-ui/context/ImpersonationContext";
import ImpersonateStudent from "./pages/ImpersonateStudent";
import ImpersonationBanner from "./actprep-ui/components/ImpersonationBanner";

// Lazy load routers - especially those loaded based on user roles
const HomeRouter = lazy(() => import("./pages/Home"));
const TestingRouter = lazy(() => import("./pages/Testing"));
const AdminRouter = lazy(() => import("./pages/Admin"));
const StudentRouter = lazy(() => import("./pages/Student"));
const InstallPage = lazy(() => import("./pages/Install"));

function AppContent() {
  const { profile, isLoaded: isProfileLoaded } = useProfile();
  const adminAccess = useAccess({ requiredGroups: ["Employees"] });
  const { isImpersonating } = useImpersonation();
  const isInstallRoute = window.location.pathname.startsWith("/install");

  if (
    !(
      isInstallRoute ||
      isProfileLoaded ||
      window.location.href.includes(urls.sso.redirect())
    )
  ) {
    return <SkeletonLayout />;
  }

  const navLinkSections = [
    {
      section: "Testing",
      icon: <Home />,
      path: urls.testing.home.base(),
      links: [
        {
          label: "Overview",
          path: urls.testing.home.dashboard(),
        },
        {
          label: "My Tests",
          path: urls.testing.home.myTests(),
        },
      ],
    },
  ];

  // Show student nav links if user is a student OR if they're impersonating one
  const showStudentNav =
    profile?.groups?.includes(groups.STUDENTS) || isImpersonating;

  if (showStudentNav) {
    navLinkSections.unshift({
      section: "Student",
      icon: <School />,
      path: urls.students.internal.base(),
      links: [
        {
          label: "Dashboard",
          path: urls.students.internal.dashboard(),
        },
        {
          label: "Week Reports",
          path: urls.students.internal.weekReports(),
        },
        {
          label: "Pre-Subject Reports",
          path: urls.students.internal.preSubjectReports(),
        },
        {
          label: "Good News",
          path: urls.students.internal.goodNews(),
        },
      ],
    });
  }

  if (adminAccess.status === "accepted") {
    navLinkSections.push({
      section: "Admin",
      icon: <Security />,
      path: urls.testing.admin.base(),
      links: [
        {
          label: "Tests",
          path: urls.testing.admin.tests(),
        },
        {
          label: "Reports",
          path: urls.testing.admin.reports(),
        },
      ],
    });
  }

  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}
    >
      <Routes>
        <Route path="/error/:errorCode/" element={<ErrorPage />} key={0} />
        <Route
          path="/oauth/callback/"
          element={<AuthenticatingPage authenticate />}
        />
        <Route
          path="/impersonate/:studentUuid/"
          element={<ImpersonateStudent />}
        />
        <Route
          path="/install/*"
          element={
            <Suspense fallback={<SkeletonLayout />}>
              <InstallPage />
            </Suspense>
          }
        />
        <Route
          path="/*"
          element={
            <>
              <AppLayout
                navlinkSections={navLinkSections}
                appTitle={"Testing"}
                injectedComponents={<ImpersonationBanner mode="student" />}
              >
                <Suspense fallback={<SkeletonLayout />}>
                  <Routes>
                    <Route path="/*" element={<HomeRouter />} />
                    <Route path="/testing/*" element={<TestingRouter />} />
                    {adminAccess.status === "accepted" && (
                      <Route path="/admin/*" element={<AdminRouter />} />
                    )}
                    {showStudentNav && (
                      <Route path="/student/*" element={<StudentRouter />} />
                    )}
                    <Route path="*" element={<Redirect to={"/error/404/"} />} />
                  </Routes>
                </Suspense>
              </AppLayout>
            </>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ImpersonationProvider userType="student">
      <AppContent />
    </ImpersonationProvider>
  );
}

export default App;
